import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import type { CalendarEventInstance } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

interface UseEventSearchResult {
  query: string;
  setQuery: (q: string) => void;
  results: CalendarEventInstance[];
  loading: boolean;
  clear: () => void;
}

export function useEventSearch(): UseEventSearchResult {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CalendarEventInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const now = new Date();
        const rangeStart = new Date(now);
        rangeStart.setMonth(rangeStart.getMonth() - 3);
        const rangeEnd = new Date(now);
        rangeEnd.setMonth(rangeEnd.getMonth() + 3);

        const result = await api.listCalendarEvents({
          rangeStart: rangeStart.toISOString(),
          rangeEnd: rangeEnd.toISOString(),
          search: trimmed,
        });
        if (!cancelled) setResults(result.events);
      } catch (err) {
        if (cancelled) return;
        console.error('[calendar search]', err);
        toast({
          title: 'Search failed',
          description: err instanceof Error ? err.message : 'Could not search events',
          variant: 'destructive',
        });
        setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return { query, setQuery, results, loading, clear };
}
